import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Bell, FileText, Package, TrendingUp, Users } from "lucide-react";
import { API, auth, LEAD_STATUSES, statusMeta } from "./crm";

export const Dashboard = ({ token, reloadKey }) => {
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/stats`, auth(token));
    setStats(data);
  }, [token]);

  useEffect(() => {
    load().catch(() => {});
  }, [load, reloadKey]);

  if (!stats)
    return (
      <p data-testid="dashboard-loading" className="text-[#6E5B4F] text-sm py-16 text-center">
        Loading dashboard…
      </p>
    );

  const cards = [
    { icon: Users, label: "Total Leads", value: stats.total_leads, sub: `+${stats.leads_this_week} this week` },
    { icon: Bell, label: "Follow-ups Due", value: stats.followups_due, sub: "today or overdue", alert: stats.followups_due > 0 },
    { icon: FileText, label: "Quotations", value: stats.quotations, sub: "sent to buyers" },
    { icon: Package, label: "Orders", value: stats.orders, sub: `${stats.orders_by_status.delivered || 0} delivered` },
  ];

  const maxSource = Math.max(1, ...Object.values(stats.by_source));

  return (
    <div data-testid="crm-dashboard" className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div
            key={c.label}
            data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`bg-white border rounded-3xl p-5 ${c.alert ? "border-red-300" : "border-[#E6DEC3]"}`}
          >
            <div className="flex items-center justify-between">
              <span className={`w-10 h-10 rounded-2xl flex items-center justify-center ${c.alert ? "bg-red-100 text-red-600" : "bg-[#EAF0EC] text-[#2E4F3E]"}`}>
                <c.icon size={18} />
              </span>
            </div>
            <p className="mt-4 font-serif font-bold text-3xl text-[#2C1A0C]">{c.value}</p>
            <p className="text-sm font-semibold text-[#2C1A0C]/80 mt-1">{c.label}</p>
            <p className="text-xs text-[#6E5B4F] mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E6DEC3] rounded-3xl p-6">
          <h3 className="font-serif font-bold text-lg text-[#2C1A0C] mb-4 flex items-center gap-2">
            <TrendingUp size={17} className="text-[#2E4F3E]" /> Leads by Status
          </h3>
          <div className="flex flex-wrap gap-2">
            {LEAD_STATUSES.map(([key, label, cls]) => (
              <span
                key={key}
                data-testid={`status-count-${key}`}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border px-3 py-1.5 ${cls}`}
              >
                {label}
                <span className="opacity-70">{stats.by_status[key] || 0}</span>
              </span>
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#E6DEC3] rounded-3xl p-6">
          <h3 className="font-serif font-bold text-lg text-[#2C1A0C] mb-4">Leads by Source</h3>
          {Object.keys(stats.by_source).length === 0 && (
            <p className="text-sm text-[#6E5B4F]">No leads yet.</p>
          )}
          <div className="space-y-3">
            {Object.entries(stats.by_source).map(([src, n]) => (
              <div key={src} data-testid={`source-bar-${src.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex justify-between text-xs font-semibold text-[#2C1A0C] mb-1">
                  <span>{src}</span>
                  <span>{n}</span>
                </div>
                <div className="h-2 rounded-full bg-[#F5EFE6] overflow-hidden">
                  <div className="h-full bg-[#2E4F3E] rounded-full" style={{ width: `${(n / maxSource) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
